import {
  FormulaCell,
  FormulaExpression,
  IFormulaExpressionEvaluator,
  StatusTrackerHistoryItem,
} from '@dpr-details/models';

import { FormulaExpressionCalculationService } from './formula-expression-calculation.service';

/**
 * Status Tracker formula calculation. Row identity is the row id — template
 * rows are stable across cards — so no anchor lookup is needed and history is
 * matched by (statusTrackerId, rowId, columnId).
 *
 * A card's cells and formulas are handed to the constructor: on the front end
 * they are already loaded with the card.
 */
export class StatusTrackerFormulaExpressionCalculationService extends FormulaExpressionCalculationService<StatusTrackerHistoryItem> {
  private readonly formulasByColumnId = new Map<number, FormulaExpression>();
  private readonly currentCardAllItems: FormulaCell[];
  private readonly statusTrackerId: number;
  private readonly templateRowIds: (number | string)[];

  constructor(
    evaluator: IFormulaExpressionEvaluator,
    formulas: FormulaExpression[],
    currentCardCells: FormulaCell[],
    statusTrackerId: number,
    templateRowIds: (number | string)[]
  ) {
    super(evaluator);
    this.currentCardAllItems = currentCardCells ?? [];
    this.statusTrackerId = statusTrackerId;
    this.templateRowIds = [...(templateRowIds ?? [])];
    for (const f of formulas ?? []) {
      if (!String.isNullOrWhiteSpace(f.expression)) {
        this.formulasByColumnId.set(f.columnId, f);
      }
    }
  }

  protected getFormula(columnId: number): FormulaExpression | null {
    return this.formulasByColumnId.get(columnId) ?? null;
  }

  protected getDetailItems(paramColumnId: number): FormulaCell[] {
    return this.currentCardAllItems.filter((d) => d.columnId === paramColumnId);
  }

  /**
   * The tracker's template rows (`statusTracker.rows`), not the rows that
   * happen to hold a cell today: a cell is only stored once it has a value or a
   * colour, so a row nobody touched would otherwise lose its carry-forward.
   */
  protected getAllRowIds(): (number | string)[] {
    return this.templateRowIds;
  }

  protected getAnchorOptionId(): number | null {
    return null;
  }

  /**
   * Scoped to this tracker template as well as the row and column: row ids are
   * numbered per template, so a cell from a different tracker can carry the
   * same pair, and the history a caller hands over spans every tracker held
   * offline.
   */
  protected getHistoryValue(
    history: StatusTrackerHistoryItem[],
    columnId: number,
    rowId: number | string
  ): string | null {
    return FormulaExpressionCalculationService.newestValue(
      (history ?? []).filter(
        (h) =>
          h.statusTrackerId === this.statusTrackerId &&
          h.columnId === columnId &&
          String(h.rowId) === String(rowId)
      )
    );
  }
}
